/* eslint-disable react/jsx-props-no-spreading */
import {
    useState,
    useCallback,
} from 'react'
import omit from 'lodash/omit'
import noop from 'lodash/noop'
import {
    GatewayByIdResponse,
} from 'app/requests/Gateway/Gateway.types'

import {
    RequestConfig,
} from 'app/types/request.types'
import {
    dateToStr,
    strToDate,
    dateTimeMask,
} from 'app/utils/date'

import useEnumValues from 'app/hooks/useEnumValues'
import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import {
    GatewayStatus,
} from 'app/enums/label.enums'
import GatewayRequests from 'app/requests/Gateway/Gateway.requests'

import {
    Gateway,
} from '../Gateways.types'
import getFields from './GatewayForm.fields'
import useStyles from './GatewayForm.styles'

type Props = {
    data?: GatewayByIdResponse,
    onSuccess?: (props: any) => void,
    onCancel?: (...args: any[]) => void,
    requestParam: RequestConfig,
    disabled?: boolean,
}

const EMPTY_OBJECT = {} as GatewayByIdResponse

const GatewayForm = ({
    data = EMPTY_OBJECT,
    onSuccess = noop,
    onCancel = noop,
    requestParam,
    disabled = false,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const exists = !!data?.id
    const [
        value,
        setValue,
    ] = useState(data)

    const fieldsWithClassName: [] = useFieldsWithClassName(
        getFields({
            gatewayStatus: value.gatewayStatus,
            getAllowedValues,
            locationId: value?.location?.id || data?.location?.id,
            showConnectionInfo: exists,
            showConnectedLogger: exists,
            disabled,
        }),
        classes,
    )

    const modifyDataBeforeSend = useCallback((newValue) => {
        const result = {
            ...newValue,
            installedOn: newValue.installedOn ? dateToStr(strToDate(newValue.installedOn), dateTimeMask) : '',
        }

        return omit(result, 'gatewayLocationCategories')
    }, [])

    const handleOnChange = useCallback((newValue: Gateway) => {
        if (String(newValue?.location?.id) !== String(data?.location?.id)) {
            if (newValue.gatewayStatus !== GatewayStatus.INSTALLED) {
                setValue({
                    ...newValue,
                    latitude: newValue?.location?.latitude,
                    longitude: newValue?.location?.longitude,
                    area: value?.location?.id !== newValue?.location.id
                        ? undefined : newValue?.area,
                })
                return
            }
            setValue({
                ...newValue,
                area: value?.location?.id !== newValue?.location.id
                    ? undefined : newValue?.area,
            })
            return
        }

        if (newValue?.longitude !== data?.longitude) {
            setValue({
                ...newValue,
                longitude: newValue?.longitude || null,
            })
            return
        }

        if (newValue?.latitude !== data?.latitude) {
            setValue({
                ...newValue,
                latitude: newValue?.latitude || null,
            })

            return
        }

        if ((newValue?.gatewayStatus !== value?.gatewayStatus)
            && ((newValue?.gatewayStatus === GatewayStatus.STOCK)
                || (newValue?.gatewayStatus === GatewayStatus.UNDER_INVESTIGATION))) {
            setValue({
                ...newValue,
                installedOn: '',
            })
            return
        }
        setValue(newValue)
    }, [
        data,
        value,
    ])

    return (
        <FormWithControls
            setExternalValue={handleOnChange}
            name={GatewayRequests.domainName}
            requestParams={requestParam}
            value={value}
            fields={fieldsWithClassName}
            classNames={{
                gridWrapper: classes.gridWrapperWithLoggers,
            }}
            modifyDataBeforeSend={modifyDataBeforeSend}
            onSuccess={onSuccess}
            exists={exists}
            disabled={disabled}
            onCancel={onCancel}
        />
    )
}

export default GatewayForm
